/**
 * Ruby Ledger admin client (plan Task 17; backend plan Task 14,
 * `admin-ruby-ledger.ts`). One line per AI run — vendor units spent → $ →
 * × multiplier → Ruby Tokens charged — plus refunds, profit and rating.
 *
 *   GET /agents/admin/ruby/ledger                     list + totals
 *   GET /agents/admin/ruby/ledger/{tenantId}/{runId}   full run + spend rows
 *   GET /agents/admin/ruby/ledger/export               CSV
 *
 * Host is `pos-agents-ext` (AdminAccess authorizer, exact super_admin claim)
 * — the same host Ruby AI Actions/Vibes/Tokens use (`rubyAi.ts`), never the
 * `pos-agents` host `transport.ts`'s `adminGet`/`authedBlob` are pinned to.
 */

import { authedRequest, tryRefreshToken } from '../apiService';
import { agentsExtUrl } from './rubyAi';

export interface LedgerTarget { type?: string; id?: string; name?: string }

export interface LedgerFeedback {
  rating: 'good' | 'bad';
  reason?: string;
  note?: string;
  at: string;
  by: string;
}

/** One row of the ledger list — a projection of the RUN# row, never the prompt or attempts. */
export interface LedgerLine {
  /** Who ran it (issue console lines: the developer). */
  actorType?: string;
  actorId?: string;
  actorEmail?: string;
  /** Free-form tags; developer lines: kind, developer, profileId/Name, model, effort, cost. */
  params?: Record<string, string | number | boolean>;
  runId: string;
  tenantId: string;
  actionId: string;
  vibeId?: string | null;
  target: LedgerTarget;
  status: string;
  settlement: string;
  providerId?: string;
  modelId?: string;
  modelVersion?: string;
  usedFallback?: boolean;
  vendorCostUsd?: number;
  multiplier: number;
  multiplierSource: string;
  rubyExact?: number;
  tokensReserved?: number;
  tokensCharged?: number;
  tokensRefunded?: number;
  chargedUsd?: number;
  /** Derived server-side: chargedUsd − vendorCostUsd (ruling R25). */
  profitUsd: number;
  feedback?: LedgerFeedback;
  createdAt: string;
  durationMs?: number;
  free?: boolean;
  vendorSpent: string;
  // --- follow-up 2026-10-05 (all optional, additive) ---
  /** Business name from the tenants table (server-resolved). */
  tenantName?: string;
  /** The merchant's selected location when the run started, and its name. */
  locationId?: string;
  locationName?: string;
  /** Where the run was started: surface label + app route. */
  source?: { surface: string; path?: string };
  /** Picture runs: the background step was skipped. */
  keepBackground?: boolean;
}

export interface LedgerTotals {
  runs: number;
  vendorCostUsd: number;
  tokensCharged: number;
  chargedUsd: number;
  profitUsd: number;
  refundedRuns: number;
  good: number;
  bad: number;
}

export interface LedgerFilters {
  fromIso?: string;
  toIso?: string;
  tenantId?: string;
  actionId?: string;
  status?: string;
  rating?: 'good' | 'bad' | 'none';
  modelId?: string;
  /** Case-insensitive substring of the item's name or id. */
  item?: string;
  /** 'developer' = issue-console spend only; 'customer' = everything else. */
  kind?: 'developer' | 'customer';
  /** Substring of the developer's email / name / id. */
  who?: string;
  /** Developer AI profile id. */
  profileId?: string;
  /** Developer lines: real cost reported, or still the dispatch estimate. */
  cost?: 'actual' | 'estimated';
  limit?: number;
  cursor?: string;
}

export interface LedgerListResponse {
  items: LedgerLine[];
  cursor?: string;
  totals: LedgerTotals;
  truncated: boolean;
  scanned?: number;
  from?: string;
  to?: string;
}

/** One vendor call recorded during the run — attempts, step/model and whether it succeeded. */
export interface LedgerRunAttempt {
  step: string;
  providerId: string;
  modelId: string;
  modelVersion?: string;
  unit: string;
  quantity: number;
  usage?: Record<string, number>;
  unitPriceUsd: number;
  costUsd: number;
  ok: boolean;
  error?: string;
  latencyMs?: number;
  at: string;
}

/** The full RUN# row, as returned by the detail endpoint (attempts + prompt + output). */
export interface LedgerRun extends Omit<LedgerLine, 'vendorSpent'> {
  clientKey?: string;
  promptFinal?: string;
  promptParts?: Record<string, string>;
  attempts: LedgerRunAttempt[];
  output?: { s3Key: string; url: string; mime: string; width: number; height: number; bytes: number };
  error?: { step: string; code: string; message: string; userMessage: string };
  /** The figure stored on the row before this read's derivation (R25) — historic rows may show 0. */
  storedProfitUsd?: number;
}

export interface LedgerSpendRow {
  pk?: string;
  sk?: string;
  runId?: string;
  occurredAt?: string;
  step?: string;
  costUsd?: number;
  [k: string]: unknown;
}

export interface LedgerRunDetail {
  run: LedgerRun;
  line: LedgerLine;
  spend: LedgerSpendRow[];
}

function ledgerQuery(filters: LedgerFilters): string {
  const p = new URLSearchParams();
  if (filters.fromIso) p.set('from', filters.fromIso);
  if (filters.toIso) p.set('to', filters.toIso);
  if (filters.tenantId) p.set('tenantId', filters.tenantId);
  if (filters.actionId) p.set('actionId', filters.actionId);
  if (filters.status) p.set('status', filters.status);
  if (filters.rating) p.set('rating', filters.rating);
  if (filters.modelId) p.set('modelId', filters.modelId);
  if (filters.item) p.set('item', filters.item);
  if (filters.kind) p.set('kind', filters.kind);
  if (filters.who) p.set('who', filters.who);
  if (filters.profileId) p.set('profileId', filters.profileId);
  if (filters.cost) p.set('cost', filters.cost);
  if (filters.limit) p.set('limit', String(filters.limit));
  if (filters.cursor) p.set('cursor', filters.cursor);
  const s = p.toString();
  return s ? `?${s}` : '';
}

export async function getRubyLedger(filters: LedgerFilters = {}): Promise<LedgerListResponse> {
  return authedRequest<LedgerListResponse>(agentsExtUrl(`/agents/admin/ruby/ledger${ledgerQuery(filters)}`));
}

/** Default page budget for {@link getRubyLedgerWindow}: 20 × 500 = 10,000 runs. */
export const LEDGER_WINDOW_MAX_PAGES = 20;

export interface LedgerWindow {
  items: LedgerLine[];
  totals: LedgerTotals;
  /** True when the page budget ran out with runs still unread — the totals undercount. */
  partial: boolean;
  pages: number;
}

const ZERO_TOTALS: LedgerTotals = {
  runs: 0, vendorCostUsd: 0, tokensCharged: 0, chargedUsd: 0, profitUsd: 0,
  refundedRuns: 0, good: 0, bad: 0,
};

export function addLedgerTotals(a: LedgerTotals, b: Partial<LedgerTotals> | undefined): LedgerTotals {
  const n = (v: unknown) => (typeof v === 'number' && Number.isFinite(v) ? v : 0);
  return {
    runs: a.runs + n(b?.runs),
    vendorCostUsd: a.vendorCostUsd + n(b?.vendorCostUsd),
    tokensCharged: a.tokensCharged + n(b?.tokensCharged),
    chargedUsd: a.chargedUsd + n(b?.chargedUsd),
    profitUsd: a.profitUsd + n(b?.profitUsd),
    refundedRuns: a.refundedRuns + n(b?.refundedRuns),
    good: a.good + n(b?.good),
    bad: a.bad + n(b?.bad),
  };
}

/**
 * Every ledger line in a window: the list endpoint's totals cover ONE page
 * only, so follow `cursor` to the end and sum the pages. Stops after
 * `maxPages` and says so (`partial: true`) rather than silently undercounting.
 */
export async function getRubyLedgerWindow(
  filters: Omit<LedgerFilters, 'cursor' | 'limit'>,
  maxPages: number = LEDGER_WINDOW_MAX_PAGES,
): Promise<LedgerWindow> {
  const items: LedgerLine[] = [];
  let totals = { ...ZERO_TOTALS };
  let cursor: string | undefined;
  let pages = 0;
  do {
    const page = await getRubyLedger({ ...filters, limit: 500, cursor });
    pages += 1;
    items.push(...(page.items ?? []));
    totals = addLedgerTotals(totals, page.totals);
    cursor = page.cursor;
  } while (cursor && pages < maxPages);
  return { items, totals, partial: !!cursor, pages };
}

export async function getRubyLedgerRun(tenantId: string, runId: string): Promise<LedgerRunDetail> {
  return authedRequest<LedgerRunDetail>(
    agentsExtUrl(`/agents/admin/ruby/ledger/${encodeURIComponent(tenantId)}/${encodeURIComponent(runId)}`),
  );
}

export interface LedgerExportResult {
  blob: Blob;
  /** `X-Ledger-Truncated: true` — the export hit the 10,000-row scan cap (MAX_EXPORT_SCAN). */
  truncated: boolean;
}

/**
 * Binary CSV download on the pos-agents-ext host — the same refresh-and-retry
 * shape as `transport.ts`'s `authedBlob`, which is pinned to pos-agents.
 * Also surfaces `X-Ledger-Truncated` so the caller can tell the operator the
 * export is partial rather than silently handing them an incomplete CSV.
 */
export async function exportRubyLedgerUrl(filters: LedgerFilters = {}): Promise<LedgerExportResult> {
  const url = agentsExtUrl(`/agents/admin/ruby/ledger/export${ledgerQuery(filters)}`);
  const token = () => (typeof localStorage !== 'undefined' ? localStorage.getItem('jwtToken') : null);
  const headers = (): Record<string, string> => {
    const t = token();
    return t ? { Authorization: `Bearer ${t}` } : {};
  };
  let res = await fetch(url, { headers: headers() });
  if (res.status === 401) {
    const refreshed = await tryRefreshToken();
    if (refreshed) res = await fetch(url, { headers: { Authorization: `Bearer ${refreshed}` } });
  }
  if (!res.ok) {
    const body = await res.text().catch(() => '');
    throw new Error(`exportRubyLedgerUrl: ${res.status} ${body}`.trim());
  }
  const truncated = res.headers.get('X-Ledger-Truncated') === 'true';
  return { blob: await res.blob(), truncated };
}

// ---------------------------------------------------------------------------
// Summary (Ledger redesign 2026-10-05) — the tab's default view

export interface LedgerSummaryTotals extends LedgerTotals {
  /** Σ rubyExact — the full × multiplier value, whole Ruby Tokens. */
  rubyValueTokens: number;
  /** Runs never billed to the merchant (Free AI / admin test). */
  freeRuns: number;
}

export interface LedgerActionSummary {
  actionId: string;
  runs: number;
  vendorCostUsd: number;
  tokensCharged: number;
  chargedUsd: number;
  profitUsd: number;
}

/** Developer spend grouped by person, profile or issue (sorted by $). */
export interface LedgerGroupSummary { key: string; label: string; runs: number; vendorCostUsd: number; estimatedRuns: number }

export interface LedgerSummaryResponse {
  totals: LedgerSummaryTotals;
  /** Sorted by vendor $ (desc) server-side. */
  byAction: LedgerActionSummary[];
  byDeveloper?: LedgerGroupSummary[];
  byProfile?: LedgerGroupSummary[];
  byIssue?: LedgerGroupSummary[];
  /** True when the server's scan budget ran out — the figures undercount. */
  partial: boolean;
  scanned?: number;
  from?: string;
  to?: string;
}

/** GET /agents/admin/ruby/ledger/summary — totals + by-action for a window (no lines). */
export async function getRubyLedgerSummary(
  filters: Pick<LedgerFilters, 'fromIso' | 'toIso' | 'tenantId'>,
): Promise<LedgerSummaryResponse> {
  return authedRequest<LedgerSummaryResponse>(agentsExtUrl(`/agents/admin/ruby/ledger/summary${ledgerQuery(filters)}`));
}

export interface LedgerMerchant { tenantId: string; name: string }

/** Every merchant (id + business name) for the Ledger's merchant type-ahead. */
export async function listLedgerMerchants(): Promise<LedgerMerchant[]> {
  const { api } = await import('../apiService');
  const tenants = await api.tenants.getAll();
  return (Array.isArray(tenants) ? tenants : [])
    .filter((t) => t && t.id)
    .map((t) => ({ tenantId: t.id, name: t.name || t.businessName || t.id }));
}
